/**
 * Small cross-component bridge so a decision card in the center of the Triptych
 * can hand the keyboard to the @yagni composer in the right rail (the answer
 * path for open-ended questions like "What hours do you want @yagni drafting
 * for you?").
 *
 * Two delivery paths, because the rail may already be mounted OR be opening in
 * response to the same click:
 *   1. A window event — caught by a composer that is already mounted and
 *      listening (the common case: the rail defaults open).
 *   2. A short-lived "requested at" timestamp — checked by a composer on mount,
 *      so a composer that mounts *because* the rail just opened still grabs
 *      focus. `focusRequestedRecently()` is false on ordinary navigation, so it
 *      never steals focus when the user simply moves between items.
 */

const EVENT = "yagni:answer-request";
const RECENT_WINDOW_MS = 800;

let lastRequestedAt = 0;

/** Test-only: clear the focus-request clock (module state survives across
 * test files when the module registry is shared — vitest `isolate: false`). */
export function __resetAnswerComposerForTests(): void {
  lastRequestedAt = 0;
}

/** Ask the @yagni composer to take focus. Safe to call outside the browser. */
export function requestComposerFocus(): void {
  if (typeof window === "undefined") return;
  lastRequestedAt = Date.now();
  window.dispatchEvent(new Event(EVENT));
}

/** Subscribe a composer to focus requests. Returns an unsubscribe fn. */
export function onComposerFocusRequest(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

/**
 * True when a focus request fired within the last {@link RECENT_WINDOW_MS} — a
 * composer that just mounted (because the rail opened) uses this to focus.
 */
export function focusRequestedRecently(): boolean {
  return Date.now() - lastRequestedAt < RECENT_WINDOW_MS;
}
